import { useRef, useState } from "react";
import { Upload, Loader2, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { btn } from "@/components/common/btn";
import { money } from "@/lib/format";
import { importProposalLines } from "@/lib/api";

const NEW = "__new__";
const stem = (name) => (name || "").replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
const inputCls = "h-7 rounded-md border border-slate-200 bg-background px-1.5 text-xs outline-none focus:border-brand";

// Header-level import: drop several vendor quotes at once; each becomes its own scope (or lands in an existing one).
export function ImportQuotesDialog({ open, onClose, scopes, masterScopes, onAdd }) {
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(0);
  const fileRef = useRef(null);

  const close = () => { setGroups([]); setBusy(0); onClose(); };
  const patch = (id, p) => setGroups((gs) => gs.map((g) => (g._id === id ? { ...g, ...p } : g)));

  const parseFiles = async (files) => {
    const list = [...(files || [])];
    if (!list.length) return;
    setBusy((b) => b + list.length);
    await Promise.all(list.map(async (f) => {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await importProposalLines(fd);
        const items = data.items || [];
        if (!items.length) { toast.error(`${f.name}: ${data.warnings?.[0] || "no line items found"}`); return; }
        setGroups((gs) => [...gs, { _id: crypto.randomUUID(), file: f.name, meta: data, items, target: NEW, name: data.vendor || stem(f.name) }]);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "could not parse"}`);
      } finally { setBusy((b) => b - 1); }
    }));
  };

  const ready = groups.filter((g) => g.target !== NEW || g.name.trim());
  const total = (g) => g.items.reduce((a, it) => a + (it.qty || 1) * (it.unit_cost || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-3xl" data-testid="import-quotes-dialog">
        <DialogHeader><DialogTitle className="text-base">Import Vendor Quotes</DialogTitle></DialogHeader>
        <button type="button" disabled={busy > 0} onClick={() => fileRef.current?.click()}
          className="flex w-full flex-col items-center gap-1.5 rounded-lg border-2 border-dashed border-slate-300 py-6 text-sm text-slate-500 hover:border-brand hover:text-slate-700" data-testid="import-quotes-dropzone">
          {busy > 0 ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <span>{busy > 0 ? `Reading ${busy} quote${busy === 1 ? "" : "s"}…` : "Choose one or more Excel, CSV or PDF vendor quotes"}</span>
          <span className="text-xs text-slate-400">Each quote becomes its own scope — or point it at an existing one below.</span>
          <input ref={fileRef} type="file" multiple accept=".xlsx,.xlsm,.csv,.tsv,.txt,.pdf" className="hidden" onChange={(e) => { parseFiles(e.target.files); e.target.value = ""; }} data-testid="import-quotes-file-input" />
        </button>
        {groups.length > 0 && (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="import-quotes-groups">
            {groups.map((g) => (
              <div key={g._id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs" data-testid={`import-quote-group-${g._id}`}>
                <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate font-medium text-slate-800" title={g.file}>{g.file}</span>
                <span className="whitespace-nowrap text-slate-500">{g.items.length} line{g.items.length === 1 ? "" : "s"} · {money(total(g))}{g.meta.source === "ai" ? " · AI" : ""}</span>
                <span className="text-slate-400">→</span>
                <select value={g.target} onChange={(e) => patch(g._id, { target: e.target.value })} className={`${inputCls} w-44`} data-testid={`import-quote-target-${g._id}`}>
                  <option value={NEW}>New scope</option>
                  {scopes.map((s, i) => <option key={s.id} value={s.id}>{String(i + 1).padStart(2, "0")} {s.name || "Untitled"}</option>)}
                </select>
                {g.target === NEW && (
                  <input value={g.name} onChange={(e) => patch(g._id, { name: e.target.value })} list="import-quote-scope-names" placeholder="Scope name" className={`${inputCls} w-48`} data-testid={`import-quote-name-${g._id}`} />
                )}
                <button type="button" aria-label="Remove" className="rounded p-1 text-slate-400 hover:text-slate-700" onClick={() => setGroups((gs) => gs.filter((x) => x._id !== g._id))} data-testid={`import-quote-remove-${g._id}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <datalist id="import-quote-scope-names">{masterScopes.map((ms) => <option key={ms.id} value={ms.name} />)}</datalist>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Matched library parts take hours and margins from the library; quote unit costs are used as-is.</span>
          <button type="button" className={btn("write")} disabled={!ready.length || busy > 0} onClick={() => { onAdd(ready); close(); }} data-testid="import-quotes-add-btn">
            Add {ready.length} Quote{ready.length === 1 ? "" : "s"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
